import { hasIncompleteScreeningName } from './screeningName';

const text = (value) => typeof value === "string" ? value.trim() : "";
const list = (value) => Array.isArray(value) ? value : [];
const selected = (value) => list(value).some((item) => !!text(item));
const finalExposureDecisions = ["no_exposure", "flagged_remediated", "flagged_confirmed"];

function hasDate(value) {
  if (!value) return false;
  try {
    const date = typeof value.toDate === "function" ? value.toDate()
      : typeof value.seconds === "number" ? new Date(value.seconds * 1000)
      : new Date(value);
    return Number.isFinite(date.getTime());
  } catch { return false; }
}

function entityProfile(ownership) {
  if (text(ownership.desktopClientEntityType)) return ownership.desktopClientEntityType;
  const flags = ownership.uboFlags || {};
  if (flags.quotedCompany) return "quoted";
  if (flags.accountHolderTrust) return "trust";
  if (flags.accountHolderFoundation) return "foundation";
  if (flags.pctNotMeaningful) return "pic";
  const mode = ownership.mobileAssistantDraft?.preliminaryMode;
  return ({ noneOfAbove: "operating", holdingActive: "holding", holdingPassive: "holding" })[mode] || mode || "";
}

function hasArrangementInfo(ownership, profile, customerName) {
  const arrangements = list(ownership.legalArrangements?.arrangements);
  return arrangements.some((arrangement) => {
    if (text(arrangement.arrangementType).toLowerCase() !== profile || !text(arrangement.name)) return false;
    // A downstream shareholder's trust does not supply the client's own details.
    if (arrangement.source !== "account_holder" &&
        !(arrangement.source !== "shareholder" && text(arrangement.name) === text(customerName))) return false;
    const parties = list(arrangement.parties);
    const roles = profile === "trust" ? ["settlor", "trustee", "beneficiary"]
      : ["founder", "council member", "beneficiary"];
    return roles.every((role) => parties.some((party) => {
      const savedRole = text(party.role).toLowerCase();
      return (savedRole === role || (role === "council member" && savedRole === "council/board")) && !!text(party.name);
    }));
  });
}

/** Completion is separate from risk: an explicitly declined relationship can be complete. */
export function evaluateKycCompletion(form, { documentsComplete = false, screeningBusy = false, useMainCountryOfOperation = false } = {}) {
  const customer = form?.customer || {};
  const ownership = form?.ownership || {};
  const funds = form?.sourceOfFundsWealth || {};
  const internal = form?.internalUse || {};
  const adverse = form?.adverseMedia || {};
  const entity = ["entity", "company"].includes(customer.type);
  const profile = entityProfile(ownership);
  const missing = [];
  const require = (condition, label) => { if (!condition) missing.push(label); };

  require(text(form?.engagement?.approach?.channel), "Engagement");
  require(text(customer.name), "Full name");
  require(!hasIncompleteScreeningName(form), "Full name required for screening");
  require(hasDate(customer.dobOrIncorporationDate), entity ? "Incorporation date" : "Date of birth");
  require(text(customer.address), "Address");
  if (!entity) require(text(customer.country), "Country of domicile");
  if (entity && useMainCountryOfOperation) require(text(customer.mainCountryOfOperation), "Country of operation");
  require(text(customer.nationalityOrIncorporationCountry), entity ? "Incorporation country" : "Nationality");
  require(/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(text(customer.email)), "Email");
  require(text(customer.businessActivity), "Business activity");
  if (entity) {
    require(["operating", "holding", "pic", "quoted", "trust", "foundation"].includes(profile), "Client entity type");
    if (["operating", "holding", "pic"].includes(profile)) {
      require(list(ownership.shareholders).some((row) => text(row?.name)), "Direct shareholder");
    }
    if (["trust", "foundation"].includes(profile)) {
      require(hasArrangementInfo(ownership, profile, customer.name), "Trust / foundation information");
    }
  }
  require(selected(funds.sourceOfWealth), "Source of wealth");
  require(text(funds.netWorthRange), "Net worth");
  require(text(funds.annualIncomeRange), "Net income");
  require(selected(funds.sourceOfFundsTypes), "Source of funds");
  require(selected(funds.paymentRoutes), "Payment route");
  require(list(ownership.signatories).some((row) =>
    [row?.name, row?.capacity, row?.nationality, row?.countryOfResidence].every(text)), "Authorised signatory");
  require(["low", "medium", "high"].includes(text(internal.geoRiskLevel).toLowerCase()), "Geographic risk");
  require([1, 2, 3].includes(Number(internal.industryRiskResult?.final?.riskLevel ?? internal.industryRiskResult?.auto?.riskLevel)), "Industry risk");
  for (const key of ["pep", "sanctions"]) {
    const screening = internal[`${key}Screening`];
    require(screening?.runAt && screening?.result, `${key} screening run`);
    require(finalExposureDecisions.includes(form?.[key]?.staffDecision), `${key} final outcome`);
  }
  require(internal.adverseMediaScreening?.runAt, "Adverse media screening run");
  require(adverse.negativeNews === false || (adverse.negativeNews === true && ["confirmed", "remediated"].includes(adverse.status)), "Adverse media final outcome");
  require(!screeningBusy, "Screening in progress");
  require(documentsComplete, "Documents checklist");
  require(["yes", "no"].includes(form?.finalOverview?.relationshipAccepted), "Relationship decision");
  return { complete: missing.length === 0, missing };
}
